/**
 * useAuth Composable - Laumeris
 * Gestion de l'authentification utilisateur
 */

import { ref, computed, readonly } from 'vue'
import { useRouter } from 'vue-router'
import { useApi } from './useApi'

// Types
export interface User {
  id: number
  email: string
  firstName: string
  lastName: string
  userType: 'owner' | 'admin' | 'comptable' | 'client'
  adminRole?: 'salarie' | 'responsable' | 'directeur'
  avatar?: string
  phone?: string
  lastLogin?: string
  createdAt: string
}

export interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
}

export interface RegisterData {
  email: string
  password: string
  firstName: string
  lastName: string
  phone?: string
  companyName?: string
}

export interface AuthResponse {
  user: User
  accessToken: string
  refreshToken: string
}

// State (singleton)
const user = ref<User | null>(null)
const isInitialized = ref(false)
const isLoading = ref(false)

export function useAuth() {
  const router = useRouter()
  const api = useApi()

  // Computed
  const isAuthenticated = computed(() => !!user.value)
  const isOwner = computed(() => user.value?.userType === 'owner')
  const isAdmin = computed(() => user.value?.userType === 'admin')
  const isComptable = computed(() => user.value?.userType === 'comptable')
  const isClient = computed(() => user.value?.userType === 'client')

  const fullName = computed(() => {
    if (!user.value) return ''
    return `${user.value.firstName} ${user.value.lastName}`
  })

  const userInitials = computed(() => {
    if (!user.value) return ''
    return `${user.value.firstName[0]}${user.value.lastName[0]}`.toUpperCase()
  })

  // Check if user has specific permission
  const hasPermission = (permission: string): boolean => {
    if (!user.value) return false

    const permissions: Record<string, string[]> = {
      owner: ['*'], // All permissions
      admin: [
        'clients:read', 'clients:write',
        'documents:read', 'documents:write',
        'invoices:read', 'invoices:write',
        'quotes:read', 'quotes:write',
        'tickets:read', 'tickets:write',
        'payments:read', 'payments:write'
      ],
      comptable: [
        'clients:read',
        'invoices:read', 'invoices:write',
        'payments:read', 'payments:write',
        'analytics:read'
      ],
      client: [
        'documents:read',
        'invoices:read',
        'tickets:read', 'tickets:write',
        'payments:read', 'payments:write'
      ]
    }

    const userPermissions = permissions[user.value.userType] || []
    return userPermissions.includes('*') || userPermissions.includes(permission)
  }

  // Check if user has any of the given roles
  const hasRole = (...roles: string[]): boolean => {
    if (!user.value) return false
    return roles.includes(user.value.userType)
  }

  // Initialize auth state from stored token
  const initialize = async (): Promise<void> => {
    if (isInitialized.value) return

    const token = api.getAccessToken()
    if (token) {
      try {
        isLoading.value = true
        const { data, success } = await api.get<{ user: User }>('/auth/me')
        if (success && data) {
          user.value = data.user
        } else {
          api.clearTokens()
        }
      } catch {
        api.clearTokens()
      } finally {
        isLoading.value = false
      }
    }

    isInitialized.value = true
  }

  // Login
  const login = async (credentials: LoginCredentials): Promise<{ success: boolean; error?: string }> => {
    isLoading.value = true

    try {
      const { data, success, error } = await api.post<AuthResponse>('/auth/login', credentials, { auth: false })

      if (success && data) {
        api.setTokens(data.accessToken, data.refreshToken)
        user.value = data.user

        // Redirect based on user type
        const redirectPath = getDefaultRoute()
        router.push(redirectPath)

        return { success: true }
      }

      return { success: false, error: error?.message || 'Erreur de connexion' }
    } catch (err) {
      return { success: false, error: 'Erreur de connexion au serveur' }
    } finally {
      isLoading.value = false
    }
  }

  // Register (for clients)
  const register = async (data: RegisterData): Promise<{ success: boolean; error?: string }> => {
    isLoading.value = true

    try {
      const { data: responseData, success, error } = await api.post<AuthResponse>('/auth/register', data, { auth: false })

      if (success && responseData) {
        api.setTokens(responseData.accessToken, responseData.refreshToken)
        user.value = responseData.user
        router.push('/dashboard')
        return { success: true }
      }

      return { success: false, error: error?.message || 'Erreur lors de l\'inscription' }
    } catch (err) {
      return { success: false, error: 'Erreur de connexion au serveur' }
    } finally {
      isLoading.value = false
    }
  }

  // Logout
  const logout = async (): Promise<void> => {
    try {
      await api.post('/auth/logout')
    } catch {
      // Ignore errors on logout
    } finally {
      api.clearTokens()
      user.value = null
      router.push('/login')
    }
  }

  // Forgot Password
  const forgotPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    isLoading.value = true

    try {
      const { success, error } = await api.post('/auth/forgot-password', { email }, { auth: false })

      if (success) {
        return { success: true }
      }

      return { success: false, error: error?.message || 'Erreur lors de l\'envoi' }
    } catch (err) {
      return { success: false, error: 'Erreur de connexion au serveur' }
    } finally {
      isLoading.value = false
    }
  }

  // Reset Password
  const resetPassword = async (token: string, password: string): Promise<{ success: boolean; error?: string }> => {
    isLoading.value = true

    try {
      const { success, error } = await api.post('/auth/reset-password', { token, password }, { auth: false })

      if (success) {
        router.push('/login')
        return { success: true }
      }

      return { success: false, error: error?.message || 'Erreur lors de la réinitialisation' }
    } catch (err) {
      return { success: false, error: 'Erreur de connexion au serveur' }
    } finally {
      isLoading.value = false
    }
  }

  // Update profile
  const updateProfile = async (profileData: Partial<User>): Promise<{ success: boolean; error?: string }> => {
    isLoading.value = true

    try {
      const { data, success, error } = await api.patch<{ user: User }>('/auth/profile', profileData)

      if (success && data) {
        user.value = data.user
        return { success: true }
      }

      return { success: false, error: error?.message || 'Erreur lors de la mise à jour' }
    } catch (err) {
      return { success: false, error: 'Erreur de connexion au serveur' }
    } finally {
      isLoading.value = false
    }
  }

  // Get default route based on user type
  const getDefaultRoute = (): string => {
    if (!user.value) return '/login'

    switch (user.value.userType) {
      case 'owner':
      case 'admin':
        return '/dashboard'
      case 'comptable':
        return '/invoices'
      case 'client':
        return '/dashboard'
      default:
        return '/dashboard'
    }
  }

  return {
    // State
    user: readonly(user),
    isAuthenticated,
    isInitialized: readonly(isInitialized),
    isLoading: readonly(isLoading),

    // Role checks
    isOwner,
    isAdmin,
    isComptable,
    isClient,

    // User info
    fullName,
    userInitials,

    // Methods
    initialize,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    updateProfile,
    hasPermission,
    hasRole,
    getDefaultRoute
  }
}
